import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { PopupRegistryImpl } from "abreact/incubator/ui/utils/PopupRegistryImpl";
import { TypeToJumpUtils } from "abreact/utils/TypeToJumpUtils";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AbIterable } from "abweb/collection/AbIterable";
import { DestroyListenerMgr } from "abweb/service/destroyListener/DestroyListenerMgr";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { addEventListener } from "abweb/utils/EventRegistration";
import { AbDimensions } from "abweb/utils/geometry/AbDimensions";
import { AbMutationObserver } from "abweb/utils/geometry/AbMutationObserver";
import { AbResizeObserver } from "abweb/utils/geometry/AbResizeObserver";
import { IdleMonitor } from "abweb/utils/IdleMonitor";
import { Lambdas } from "abweb/utils/lambda/Lambdas";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { Supplier } from "abweb/utils/Supplier";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";
import { useCallback, useEffect } from "react";
import { tabbable } from "tabbable";
const LOGGER = LogUtils.getLogger(__filename);
const noValue = Symbol("lazyRef.noValue");
/* Simple quadratic easing function. */
const ease = x => x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;

/**
 * Helper function for useAnimate. Invokes recursively until duration is expended or cancelObject.current is true.
 */
const animate = (startTime, duration, f, cancelObject) => {
  if (cancelObject.current) return; // If canceled, do nothing
  const timePassed = new Date().getTime() - startTime; // Calculate time since animation start
  f(timePassed >= duration ? 1 : ease(timePassed / duration)); // Invoke animation function with 1 if animation is done, otherwise use ease function
  if (timePassed < duration) window.requestAnimationFrame(() => animate(startTime, duration, f, cancelObject)); // Recurse if animation is not done
};

/**
 * Returns a unique id.
 */
export const useUniqueId = prefix => {
  return useLazyRef(() => TypeUtils.generateUniqueId(prefix)).current;
};

/**
 * Returns a function to call when a valid key is pressed, in order to "jump" (shift focus, selection, etc) to
 * an item based on that keypress.
 * When called, the returned function will run the jumpFunction with input i, where i is the result of chooserFunction
 * based on a sequence of most recently-typed characters.
 * @param chooserFunction Calculates the item to jump to based on a sequence of previously-typed characters and a
 * newly-typed character. Must return an array containing a number indicating the index of the chosen item (or -1 if none
 * found), and the new string of recently-typed characters.
 * @param jumpFunction Function to call to "jump" to an item given its index.
 * @return Function to call when a valid key is pressed. The returned function will run the jumpFunction with input i,
 * where i is the result of chooserFunction based on a sequence of most recently-typed characters. When this function
 * is called, its newChar parameter should either be a single-character string, or undefined to clear the string of
 * recently-typed characters.
 */
export const useTypeToJump = (chooserFunction, jumpFunction) => {
  const typedChars = React.useRef("");
  return char => {
    // for now, space is not used as part of the type-to-jump string
    if (char === " ") {
      return;
    }
    if (char === undefined) {
      typedChars.current = "";
      return;
    }
    const [index, newString] = chooserFunction(typedChars.current, char);
    if (index !== -1) {
      jumpFunction(index);
    }
    typedChars.current = newString;
  };
};

/**
 * Wrapper over a common pattern to use a singleton for the life of a component.
 * Accepts an optional cleanup function.
 */
export function useSingleton(factory, cleanup) {
  return useCachedValueWithCleanup(factory, cleanup ?? (() => {}), []);
}

/**
 * useCachedValue is a useMemo replacement - takes an update method and a list of dependencies and returns a ref that will be recalculated
 * using the update method only if one of the dependencies changes (defined by triple equals predicate).
 * useMemo does not guarantee that the value will not get reevaluated even if the dependencies don't change.
 * @param valueFunction is the update function
 * @param dependencies is an array of dependencies that drive the update of the value ref return value
 * @return value ref that is updated only when one of the dependencies is updated
 */
export function useCachedValue(valueFunction, dependencies) {
  return useCachedValueWithCleanup(valueFunction, () => {}, dependencies);
}

/**
 * Similar to React.useMemo / useCachedValue, but lets you execute some cleanup every time the value is
 * recomputed (and after it is disposed on unmount).
 */
export function useCachedValueWithCleanup(factory, cleanup, dependencies) {
  const previousDependencies = React.useRef([]);
  const valueRef = React.useRef(noValue);
  if (valueRef.current === noValue || !ArrayUtils.isEquals(dependencies, previousDependencies.current)) {
    if (valueRef.current !== noValue) {
      cleanup(valueRef.current);
    }
    valueRef.current = factory();
    previousDependencies.current = dependencies;
  }
  useOnUnmount(() => {
    if (valueRef.current !== noValue) {
      cleanup(valueRef.current);
      valueRef.current = noValue;
    }
  });
  return valueRef.current;
}

/**
 * Default type-to-jump function. Manual testing shows noticeable delay starting around 50,000 items.
 * @param items Items in the collection component calling the hook.
 * @param getCurrentIndex Function to return index of the component's currently-chosen item.
 * @param jumpFunction Function which "jumps to" (e.g. selects or focuses) the item at the given index.
 * @param itemToString Function to convert an item in items to a string.
 * @param dataFreshness Only recompute the memoized value when this changes.
 * @return Function to call when a valid key is pressed. This function chooses an item whose string representation
 * has a prefix matching a sequence of recently-typed characters and jumps to said item.
 */
export const useDefaultTypeToJump = (items, getCurrentIndex, jumpFunction, itemToString, dataFreshness) => {
  return useTypeToJump(TypeToJumpUtils.useTypeToJumpPrefixChooser(items, getCurrentIndex, itemToString, dataFreshness), jumpFunction);
};

/**
 * Returns a forceUpdate function.
 * Usage:
 * const forceUpdate = AbHooks.useForceUpdate()
 */
export const useForceUpdate = () => {
  const [, setCount] = React.useState(0);
  return useCallback(() => setCount(n => n + 1), []);
};

/**
 * Calls the provided onMount function when component mounts.
 * Usage:
 * AbHooks.useOnMount(() => {
 *   important mount things
 * })
 */
export const useOnMount = onMount => {
  // eslint-disable-next-line abinitio/exhaustive-deps
  React.useEffect(onMount, []);
};

/**
 * Calls the provided onWillMount function child components are instantiated.  This is needed if  a parent component
 * needs to execute logic before the child components are instantiated (e.g., put a ReactContext in scope that the children
 * required).  Note that  useOnMount is only invoked AFTER the children have been instantiated
 * (in fact, the children onMount functions will be invoked before the parent onMount function!).
 * Usage:
 * AbHooks.useOnWillMount(() => {
 *   important pre-mount things
 * })
 */
export const useOnWillMount = onWillMountCallback => {
  // Inspired by: https://stackoverflow.com/questions/62091146/componentwillmount-for-react-functional-component
  const mounted = React.useRef(false); // useRef to avoid extra dom render
  if (!mounted.current) {
    onWillMountCallback();
    mounted.current = true;
  }
};

/**
 * Calls the provided onUnmount function when component unmounts.
 * Usage:
 * AbHooks.useOnUnmount(() => {
 *   important unmount things
 * })
 */
export const useOnUnmount = onUnmount => {
  const currentOnUnmount = useCurrentCallback(onUnmount);
  React.useEffect(() => currentOnUnmount, []);
};

/**
 * Calls the provided onMount function when component mounts.
 * Usage:
 * AbHooks.useOnMount(() => {
 *   important mount things
 * })
 */
export const useLayoutOnMount = onMount => {
  // eslint-disable-next-line abinitio/exhaustive-deps
  React.useLayoutEffect(onMount, []);
};

/**
 * Same as useEffect, but not called on initial render.
 */
export const useOnValueChange = (onUpdate, value) => {
  useOnValuesChange(onUpdate, [value]);
};

/**
 * Same as useEffect, but not called on initial render.
 */
export const useOnValuesChange = (onUpdate, values) => {
  const firstRender = React.useRef(true);
  const currentOnUpdate = useCurrentCallback(onUpdate);
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      currentOnUpdate();
    }
    // eslint-disable-next-line abinitio/exhaustive-deps
  }, values);
};

/**
 * Returns a Ref whose value indicates whether the current component is mounted.
 * Does not rerender when isMounted changes.
 * Should be marked STABLE
 */
export const useIsMounted = () => {
  const isMounted = React.useRef(false);
  useOnMount(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  });
  return isMounted;
};

/**
 * Allows for a lazy ref initialized only once.
 * Usage:
 * const thing = AbHooks.useLazyRef(() => new ExpensiveInitialization())
 * As opposed to:
 * const thing = React.useRef(new ExpensiveInitialization())
 */
export const useLazyRef = init => {
  const ref = React.useRef(noValue);
  const currentInit = useCurrentCallback(init);
  const objectRef = React.useRef({
    get current() {
      if (ref.current === noValue) {
        ref.current = currentInit();
      }
      return ref.current;
    },
    set current(value) {
      ref.current = value;
    }
  });
  return objectRef.current;
};
export const useAutoSelect = props => {
  React.useEffect(() => {
    if (props.autoSelect && props.value === undefined) {
      const items = AbIterable.first(props.items, 2);
      if (items.length === 1 && items[0] !== AB_ITEM_SEPARATOR && items[0] !== undefined) {
        props.onChange(items[0]);
      }
    }
  });
};

/**
 * Returns a useStaticState function that behaves identically to useState,
 * but state is persisted statically, so that if the component is unmounted
 * and remounted, the state will remain.
 *
 * Nothing complex should be persisted in static state. Primitives are recommended.
 * Any objects persisted should be extremely well-understood and totally context-independent.
 * Complex objects are extremely likely to not behave
 * properly after being stored in static state and restored.
 *
 * @param componentName the name of the component
 * @param instanceKey a unique key for this instance of the component. If all instances
 *        of this component should share the same state, leave this undefined.
 *
 * Usage:
 * const useStaticState = AbHooks.useStaticStateFactory("MyComponent", instanceID);
 * const [myState, setMyState] = useStaticState("initialStateValue");
 */
const staticStateMap = new Map();
const listenersForKey = new Map();
export const useStaticStateFactory = (componentName, instanceKey) => {
  const invocationIndex = React.useRef(0);
  const key = instanceKey ? `${componentName}-${instanceKey}` : componentName;
  const onStateUpdated = useForceUpdate();
  useEffect(() => {
    const listeners = listenersForKey.get(key) ?? new Set();
    const myListener = {
      onStateUpdated
    };
    listeners.add(myListener);
    listenersForKey.set(key, listeners);
    return () => {
      listeners.delete(myListener);
    };
  }, [key]);
  if (!staticStateMap.has(key)) staticStateMap.set(key, []);
  const localState = staticStateMap.get(key);
  Assert.assert(localState != null, `Expect localState for key ${key}`);
  return initialState => {
    //this is a meta-hook, so this hook invocation is actually valid.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const myIndex = useLazyRef(() => invocationIndex.current++).current;
    const setter = newVal => {
      localState[myIndex] = newVal;
      listenersForKey.get(key)?.forEach(listener => listener.onStateUpdated());
    };
    if (myIndex >= localState.length) {
      //should always be the end of the array
      localState[myIndex] = Supplier.resolve(initialState);
    }
    return [localState[myIndex], setter];
  };
};

/**
 * Invokes the handler function repeatedly every intervalMs ms. By default, waits intervalMs before first invocation.
 * Automatically cancels when the component unmounts.
 * @param handler the function to repeatedly invoke. If returning boolean, or Promise<boolean>, true means the interval should be cancelled.
 * @param intervalMs the interval in milliseconds at which to invoke the handler.
 * @param intervalActive sets whether the interval should be running. Temporarily or permanently cancel the interval by setting this to false.
 * @param runImmediately if true, handler will run immediately when it becomes active and then every intervalMs after that.
 * @param idleTimeoutMs specifies the number of idle milliseconds after which the interval will pause executing. The interval will resume after awake.
 * @param onIdle optional callback invoked when the user transitions from active to idle
 *
 * returns a function that can be used to cancel the interval early if desired.
 */
export const useInterval = (handler, intervalMs, intervalActive = true, runImmediately = true, idleTimeoutMs = 5 * 60 * 1000, onIdle) => {
  const userIsIdle = useUserIsIdle(idleTimeoutMs, null, onIdle);
  const currentHandler = useCurrentCallback(handler);
  const currentRunImmediately = useCurrentValue(runImmediately);
  React.useEffect(() => {
    if (intervalActive && !userIsIdle) {
      //if we're here we just started up, just woke up from idle, or just became active.
      if (currentRunImmediately.current) currentHandler();
      const handle = setInterval(currentHandler, intervalMs);
      return () => clearInterval(handle);
    }
  }, [intervalActive, userIsIdle, intervalMs]);
};

/**
 * A wrapper around setTimeout that will automatically cancel timeouts on unmount.
 * Usage:
 * const safeTimeout = useSafeTimeout()
 * safeTimeout(() => {
 *   // Do Stuff
 * }, 1000)
 */
export const useSafeTimeout = () => {
  const timeoutSet = useLazyRef(() => new Set()).current;
  useOnUnmount(() => {
    timeoutSet.forEach(clearTimeout);
  });
  return (handler, timeoutMillis) => {
    const timeout = setTimeout(() => {
      timeoutSet.delete(timeout);
      handler();
    }, timeoutMillis);
    timeoutSet.add(timeout);
    return timeout;
  };
};

/* Use an exported class so that tests can set this value. */
export class AbHooksErrorTimeout {}
_defineProperty(AbHooksErrorTimeout, "mSec", 3000);
export const useErrorTimeout = (showError, timeoutMillis = AbHooksErrorTimeout.mSec) => {
  const timeoutRef = React.useRef(undefined);
  useOnUnmount(() => clearTimeout(timeoutRef.current));
  return {
    start: () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        showError();
        timeoutRef.current = undefined;
      }, timeoutMillis);
    },
    clear: () => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };
};

/**
 * Used when you need to observe the parent of an html element resizing instead of an element itself.
 */
export const useParentResizeObserver = onResize => {
  const dimensionsRef = React.useRef(undefined);
  const currentOnResize = useCurrentCallback(onResize);
  const parentRef = useResizeObserver((newDimensions, element) => {
    const flooredDimensions = AbDimensions.floor(newDimensions); // ResizeObserver already takes padding into account, so no need to apply it again
    if (!AbDimensions.equal(flooredDimensions, dimensionsRef.current)) {
      dimensionsRef.current = flooredDimensions;
      currentOnResize(flooredDimensions, element);
    }
  });
  return element => {
    parentRef.current = element?.parentElement ?? null;
    if (!dimensionsRef.current && parentRef.current) {
      const flooredDimensions = AbDimensions.fromElementContentBox(parentRef.current);
      dimensionsRef.current = flooredDimensions;
      currentOnResize(flooredDimensions, parentRef.current);
    }
  };
};

/**
 * Wrapper around annoying ResizeObserver interface. The argument is the callback for when the observed element resizes.
 * The hook returns a ref that should be put on the desired element.
 * 
 * NOTE: Does not invoke onResize when the element is hidden by display: none and the like.
 * @param onResize callback invoked when the returned ref resizes.
 * @param skipFirstCallback used for when you don't want the first resize call to do anything.
 */
export const useResizeObserver = (onResize, skipFirstCallback = false) => {
  useOnUnmount(() => observer.current.disconnect());
  const currentOnResize = useCurrentCallback(onResize);
  const currentSkipFirstCallback = useCurrentValue(skipFirstCallback);
  const isFirstCallback = React.useRef(true);
  const observer = useLazyRef(() => new AbResizeObserver((dimensions, observed, isHidden) => {
    if (isFirstCallback.current) {
      isFirstCallback.current = false;
      if (currentSkipFirstCallback.current) return;
    }
    if (dimensions.width === 0 && dimensions.height === 0 && isHidden) return;
    currentOnResize(dimensions, observed);
  }));
  return React.useRef({
    get current() {
      return observer.current.observedElement;
    },
    set current(value) {
      // Do not allow "null" to be observed. Sometimes React tries to do this, but we don't want it.
      if (value === null) return;

      // Reset "is first" if we're observing a different element from before
      if (observer.current.observedElement !== value) {
        isFirstCallback.current = true;
      } else {
        // Don't re-observe what we're already observing
        return;
      }
      observer.current.observe(value);
    }
  }).current;
};

/**
 *
 * @param onMutation Callback that gets called when there is a mutation
 * @param skipFirstCallback Do not call the callback on the first time the ref is set to a non-null value.
 * @param options Options for the mutation observer.
 */
export const useMutationObserver = (onMutation, skipFirstCallback, options) => {
  useOnUnmount(() => observer.current.disconnect());
  const currentOnMutation = useCurrentCallback(onMutation);
  const currentSkipFirstCallback = useCurrentValue(skipFirstCallback ?? false);
  const currentOptions = useCurrentValue(options);
  const isFirstCallback = React.useRef(true);
  const observer = useLazyRef(() => {
    return new AbMutationObserver(currentOnMutation);
  });
  return {
    get current() {
      return observer.current.observedElement;
    },
    set current(value) {
      // Do not allow "null" to be observed. Sometimes React tries to do this but we don't want it.
      if (value === null) return;

      // Reset "is first" if we're observing a different element from before
      if (observer.current.observedElement !== value) {
        isFirstCallback.current = true;
      } else {
        // Don't re-observe if it's the same element
        return;
      }
      if (isFirstCallback.current) {
        isFirstCallback.current = false;
        if (!currentSkipFirstCallback.current) onMutation(value);
      }
      observer.current.observe(value, currentOptions.current);
    }
  };
};

/**
 * Returns whether the user is currently considered idle.
 * @param timeoutMs the number of milliseconds of idleness after which onIdle will be called. Defaults to 5 minutes.
 * @param rpcServiceToObserve see IdleMonitor.ts rpcServiceToObserve
 * @param onIdle optional callback invoked when the user transitions from active to idle
 */
export const useUserIsIdle = (timeoutMs = 5 * 60 * 1000, rpcServiceToObserve = null, onIdle) => {
  const [idle, setIdle] = React.useState(false);
  const onIdleCallback = useCurrentCallback(() => {
    setIdle(true);
    onIdle?.();
  });
  const onAwake = () => setIdle(false);
  React.useEffect(() => {
    const handle = IdleMonitor.addListener(timeoutMs, onIdleCallback, onAwake, rpcServiceToObserve);
    return () => IdleMonitor.removeListener(handle);
  }, [timeoutMs, rpcServiceToObserve]);
  return idle;
};

/**
 * When hooks must provide a callback function to other external services that are not hook-based,
 * it is common for the provided callback to get "stale" relative to the current render state,
 * since the callback is not re-created with each render.
 *
 * This wrapper takes a function and wraps it so that it will not get stale. The external service will
 * call the most modern version of the provided function.
 * Should be marked STABLE
 */
export const useCurrentCallback = rawFunc => {
  const currentFunction = useCurrentValue(rawFunc);
  return React.useCallback((...args) => {
    return currentFunction.current(...args);
  }, []);
};

/**
 * Should be marked STABLE
 */
export const useCurrentValue = value => {
  const valueRef = React.useRef(value);
  valueRef.current = value;
  return valueRef;
};

/**
 * Used to invoke animationFunction every animation frame with an ease function applied. Every time value changes,
 * useAnimate will gradually invoke animationFunction until it invokes with the new value. Duration is in millis.
 */
export const useAnimate = (value, duration, animationFunction) => {
  const currentValueRef = React.useRef(0); // Tracks animation progress
  const previousValueRef = React.useRef(0); // Stores start point for each animation invocation
  const cancelObjectRef = React.useRef({
    current: false
  });
  const currentAnimationFunction = useCurrentCallback(animationFunction);

  // Effect kicks off animation to new value every time value changes
  React.useEffect(() => {
    animate(new Date().getTime(), duration, frac => {
      if (frac === 1) {
        currentAnimationFunction(value);
        currentValueRef.current = value;
        return;
      }
      const delta = (value - previousValueRef.current) * frac;
      currentAnimationFunction(previousValueRef.current + delta);
      currentValueRef.current = previousValueRef.current + delta;
    }, cancelObjectRef.current);
    return () => {
      previousValueRef.current = currentValueRef.current;
      cancelObjectRef.current.current = true;
      cancelObjectRef.current = {
        current: false
      };
    };
  }, [value, duration]);
};

/**
 * At times, it is helpful to have both a state value/setter as well as a synchronized ref.
 * This is mostly used when callbacks want to refer to the state value, and it has been stale.
 * @param initialValue
 * Note: in abinitio/exhaustive-deps, setState and ref are marked STABLE */
export const useStateWithRef = initialValue => {
  const [state, setState] = React.useState(initialValue);
  const ref = React.useRef(state);
  // Replace setState with a version that modifies both the state and the ref.
  const setStateAndRef = React.useCallback(action => {
    // If the action is a function, execute it to get the value so we can set it in the ref.
    const newValue = typeof action === "function" ? action(ref.current) : action;
    ref.current = newValue;
    setState(newValue);
  }, [setState]);
  return [state, setStateAndRef, ref];
};
export const useStringValidation = props => {
  const onChange = newValue => {
    if (newValue === undefined && props.value === undefined) return;
    if (newValue !== undefined && props.value !== undefined && props.equals(newValue, props.value)) return;
    props.onChange(newValue);
  };
  const format = value => value === undefined ? "" : props.format.format(value);
  const calcInitialInputString = () => {
    if (props.value !== undefined) return format(props.value);
    if (props.invalidString === undefined) return "";
    const parsed = props.format.parse(props.invalidString);
    if (ClassUtils.isInstanceOf(parsed, Error)) {
      return props.invalidString;
    }
    LOGGER.warn(`invalidString prop '${props.invalidString}' should not be valid.`);
    window.requestAnimationFrame(() => {
      onChange(parsed);
    });
    return "";
  };
  const [inputString, setInputString, inputStringRef] = useStateWithRef(calcInitialInputString);
  const calcInitialTextIsValid = () => {
    if (props.value !== undefined) return true;
    if (inputStringRef.current.length > 0) return false;
    return true;
  };
  const [textIsValid, setTextIsValid] = React.useState(calcInitialTextIsValid);
  const [inRange, setInRange] = React.useState(props.value === undefined ? true : props.inRange(props.value));
  const valueRef = useCurrentValue(props.value);
  const timeoutRef = useErrorTimeout(() => {
    const parsed = props.format.parse(inputStringRef.current);
    if (ClassUtils.isInstanceOf(parsed, Error)) {
      setTextIsValid(false);
    } else if (!props.inRange(parsed)) {
      setTextIsValid(true);
      setInRange(false);
    }
  });
  const inputStringMatchesValueProp = () => {
    if (!inputStringRef.current) return valueRef.current === undefined;
    const parsed = props.format.parse(inputStringRef.current);
    if (ClassUtils.isInstanceOf(parsed, Error) || !props.inRange(parsed)) return true;
    if (valueRef.current === undefined) return false;
    return props.equals(parsed, valueRef.current);
  };
  const throwErrorIfMismatch = () => {
    if (process.env.NODE_ENV !== "development") return; // Return if not in dev mode
    if (!inputStringMatchesValueProp()) {
      throw new Error(`props.onChange was invoked with '${inputStringRef.current}' but props.value was not updated. It is still '${format(valueRef.current)}'`);
    }
  };
  const mismatchTimeoutRef = useErrorTimeout(throwErrorIfMismatch, 1500);
  useOnValueChange(() => {
    const parsedInputString = props.format.parse(inputStringRef.current);
    const isInputStringParseError = ClassUtils.isInstanceOf(parsedInputString, Error);
    const isInputStringInRange = isInputStringParseError ? false : props.inRange(parsedInputString);
    if (props.value === undefined && (isInputStringParseError || !isInputStringInRange)) {
      // incoming "undefined" value could have been set by this hook on invalid input, or by the consumer,
      // so don't process this ambiguous case for now. Consumers will have to use the ref to clear out the input.
      // See ABSDK-2682 for details
      return;
    }
    timeoutRef.clear();
    /**
     * If {@link inputString} is the empty string, that means that the input value was most likely undefined (see
     * {@link format}). If the old value, {@link inputString} is undefined and the new value, {@link props.value} is
     * defined, we assert it is a fair assumption that, in all cases, we want to update {@link inputString}.
     *
     * This corrects an issue where, when the empty string gets parsed to a zero-like value, setting {@link props.value}
     * to an actual zero value results in a (undesirable) positive equals test, which was preventing the updating
     * of the empty string value {@link props.value}. Initially surfaced in ABSDK-2500.
     */
    if (props.value === undefined || isInputStringParseError || inputStringRef.current === "" || !props.equals(parsedInputString, props.value)) {
      setInputString(format(props.value));
    }
    throwErrorIfMismatch();
    setTextIsValid(true);
    setInRange(props.value ? props.inRange(props.value) : true);
  }, format(props.value));
  useOnValueChange(() => {
    const currentString = props.format.parse(inputStringRef.current);
    if (ClassUtils.isInstanceOf(currentString, Error)) {
      setInRange(props.value ? props.inRange(props.value) : true);
    } else {
      setInRange(props.inRange(currentString));
    }
  }, props.inRange);
  const onCommit = (commitString, overwriteText = false) => {
    if (!commitString) {
      setTextIsValid(true);
      setInRange(true);
      onChange(undefined);
      mismatchTimeoutRef.start();
      return;
    }
    const newValue = props.format.parse(commitString);
    if (ClassUtils.isInstanceOf(newValue, Error)) {
      setTextIsValid(false);
      return;
    }
    setTextIsValid(true);
    if (overwriteText) setInputString(format(newValue));
    if (!props.inRange(newValue)) {
      setInRange(false);
      return;
    }
    setInRange(true);
    onChange(newValue);
    mismatchTimeoutRef.start();
  };
  const textOnChange = s => {
    setInputString(s);
    const parsed = props.format.parse(s);
    if (!s || !ClassUtils.isInstanceOf(parsed, Error) && props.inRange(parsed)) {
      timeoutRef.clear();
      onCommit(s);
    } else {
      mismatchTimeoutRef.clear();
      timeoutRef.start();
      onChange(undefined);
    }
  };
  return {
    textIsValid,
    inRange,
    inputString,
    textOnChange,
    onCommit
  };
};

/**
 * Passes a new value into a ref, whether it is a function or an object.
 */
export const invokeRef = (ref, value) => {
  if (typeof ref === "function") ref(value);else ref.current = value;
};

/**
 * Utility for synchronizing parts of a RefT with multiple underlying refs.
 * It takes in the abRef, a stateful partial for the RefT, the key to be modified,
 * and a function which can check if the partial has been made whole. See AbFilteredList.
 * Should be marked STABLE
 */
export const useComposeRef = (props, partial, key, isWhole) => {
  return useCurrentCallback(element => {
    const wasComplete = isWhole(partial);
    partial[key] = element ?? undefined;
    const isComplete = isWhole(partial);
    if (!props.abRef) return;
    if (isComplete) invokeRef(props.abRef, partial);
    if (!isComplete && wasComplete) invokeRef(props.abRef, null);
  });
};

/**
 * Utility for synchronizing a provided AbInputRef with an HTMLElement.
 */
export const useInputRef = props => {
  return element => {
    if (!props.abRef) return;
    if (element) {
      invokeRef(props.abRef, {
        inputElement: element
      });
    } else {
      invokeRef(props.abRef, null);
    }
  };
};
export const useThrottling = (f, delayMillis = 200, runEvenIfUnmounted = false) => {
  const throttled = React.useRef(false);
  const callAfterThrottle = React.useRef(false);
  const currentF = useCurrentCallback(f);
  const isMountedRef = useIsMounted();
  const throttledRef = React.useRef(() => {
    if (throttled.current) {
      callAfterThrottle.current = true;
      return;
    }
    currentF();
    throttled.current = true;
    callAfterThrottle.current = false;
    setTimeout(() => {
      throttled.current = false;
      if ((runEvenIfUnmounted || isMountedRef.current) && callAfterThrottle.current) {
        throttledRef.current();
      }
    }, delayMillis);
  });
  return throttledRef.current;
};
export const useWindowSize = (throttleMillis = 200, view) => {
  const definedView = React.useRef(view ?? window).current;
  const [windowSize, setWindowSize] = React.useState({
    windowWidth: definedView.innerWidth,
    windowHeight: definedView.innerHeight
  });
  const throttled = useThrottling(() => {
    setWindowSize({
      windowWidth: definedView.innerWidth,
      windowHeight: definedView.innerHeight
    });
  }, throttleMillis);
  useOnMount(() => {
    return addEventListener(definedView, "resize", throttled);
  });
  return windowSize;
};

/**
 * @return A ref containing a {@link PopupRegistry}
 * This popup registry can be supplied in PopupProps when showing popups with {@link PopupManager#show}.
 * Any popups raised with this registry supplied will be closed when the component calling this hook is unmounted.
 */
export const usePopupRegistry = () => {
  const destroyListenerRef = useLazyRef(() => new DestroyListenerMgr());
  const popupRegistryRef = useLazyRef(() => new PopupRegistryImpl(destroyListenerRef.current));
  useOnUnmount(() => {
    destroyListenerRef.current.destroyAll();
  });
  return popupRegistryRef;
};

// Changed by soliphant. React's default typing of forwardRef doesn't handle generic components well.
// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref
export const forwardRef = React.forwardRef;

/**
 * Options used for the useManagedState... hooks.
 */

/**
 * Readonly version of React's MutableRefObject. Note that React's RefObject
 * does not work because it force-allows null (current: T | null).
 */

/**
 * Makes externally managed state available to React components as a stateful value.
 * Returns a React Ref so the stateful value can be used safely in event handlers.
 *
 * @param initialState The initial value to make available (can also be a supplier function).
 * @param subscriber A function to subscribe to changes in state.
 * @param options Optional parameters, such as logging information.
 */
export function useManagedState(initialState, subscriber, options) {
  return useManagedStateWithTransform(initialState, subscriber, Lambdas.identity, options);
}

/**
 * Makes externally managed state available to React components as a stateful value.
 * Returns a React Ref so the stateful value can be used safely in event handlers.
 * Allows for the subscription callback to accept a different type than the state.
 *
 * @param initialState The initial value to make available (can also be a supplier function).
 * @param subscriber A function to subscribe to changes in state.
 * @param transform Used to convert the callback type to the state type.
 * @param options Optional parameters, such as logging information.
 */
export function useManagedStateWithTransform(initialState, subscriber, transform, options) {
  const [_, setState, stateRef] = useStateWithRef(initialState);
  // Manage listener subscription.
  useCachedValueWithCleanup(() => {
    options?.logging?.log.debug("[useManagedState] Adding a listener for " + options.logging.logIdentifier);
    return subscriber((...args) => {
      options?.logging?.log.debug("[useManagedState] Got a new value for " + options.logging.logIdentifier);
      const newState = transform(...args);
      setState(newState);
    });
  }, unsubscribe => {
    options?.logging?.log.debug("[useManagedState] Removing a listener for " + options.logging.logIdentifier);
    unsubscribe();
  }, []);
  return stateRef;
}

/**
 * Used to only allow one call of a long-running async function to actually resolve. The callback has access to an
 * object with a "stale" property to indicate whether another call has occurred. This also allows the user to
 * show a waiting icon while the call is in flight.
 * Note: in abinitio/exhaustive-deps, the first return value is marked STABLE
 */
export const useSynchronizedCallback = callback => {
  const idRef = React.useRef(0);
  const [inFlight, setInFlight] = React.useState(false);
  const isMountedRef = useIsMounted();
  const currentCallback = useCurrentCallback(callback);
  const staleObjectRef = React.useRef({
    stale: false
  });
  const wrappedFunc = React.useCallback((...args) => {
    const localId = ++idRef.current;
    staleObjectRef.current.stale = true;
    staleObjectRef.current = {
      stale: false
    };
    const value = currentCallback(staleObjectRef.current, ...args);
    if (value) {
      setInFlight(true);
      return value.finally(() => {
        if (localId === idRef.current) {
          if (!isMountedRef.current) return;
          setInFlight(false);
        }
      });
    }
    setInFlight(false);
    return undefined;
  }, []);
  return [wrappedFunc, inFlight];
};

//doesn't display anything that's stale.
export function useAsyncMemo(computeFunc, deps) {
  const [result, setResult] = React.useState();
  const updateCount = React.useRef(0);
  useEffect(() => {
    updateCount.current++;
    const initialUpdateCount = updateCount.current;
    computeFunc().then(newResult => {
      const isLatestUpdate = updateCount.current === initialUpdateCount;
      if (isLatestUpdate) {
        setResult(newResult);
      }
    });
    return () => setResult(undefined);
    // eslint-disable-next-line abinitio/exhaustive-deps
  }, deps);
  return result;
}

/**
 * Mutates children of ref to have no tab stops by setting tabIndex to -1. 
 */
export const useNoTabStops = () => {
  return useMutationObserver(rootElement => {
    const tabbableElements = tabbable(rootElement, {
      includeContainer: false
    });
    tabbableElements.forEach(element => {
      element.tabIndex = -1;
    });
  }, false, {
    subtree: true,
    childList: true,
    characterData: true,
    attributeFilter: ["tabindex", "disabled"] // These are attributes that could impact what is tabbable
  });
};

/**
 * Returns a memoized version of the specified method of object that is properly bound to the object.
 * Useful when using such methods as dependencies in other hooks.
 */
export function useObjectMethod(object, method) {
  const func = object[method];
  if (typeof func !== "function") {
    throw new Error(`${String(method)} should be a method`);
  }
  return React.useMemo(() => {
    return func.bind(object);
  }, [object, func]);
}

/**
 * Returns a stable callback that re-throws an error during the next React render pass,
 * allowing it to be caught by the nearest error boundary (error boundaries only catch
 * errors thrown during render).
 *
 * Usage: `.catch(throwAsyncError)` or `catch (e) { throwAsyncError(e); }`
 */
export function useThrowAsyncError() {
  const [, setState] = React.useState();
  return React.useCallback(e => {
    setState(() => {
      throw e;
    });
  }, []);
}
//# sourceMappingURL=AbHooks.js.map
